// AdminTrades — Live trade monitoring
// All open trades across all users, with ability to force-close

import { useState } from 'react';

interface Trade {
  id: string; user: string; userId: string; pair: string; direction: 'long' | 'short';
  qty: number; entry: number; current: number; pnl: number; pnlPct: number;
  opened: string; platform: string; bot: string; risk: number; flagged?: boolean;
}

const TRADES: Trade[] = [
  { id: 't1', user: 'Marcus Chen',  userId: 'u2', pair: 'NQ', direction: 'long',  qty: 3, entry: 18245, current: 18278, pnl: 495,  pnlPct: 0.18, opened: '14m ago',  platform: 'Rithmic',   bot: 'Breakout',         risk: 1.0 },
  { id: 't2', user: 'Sofia Petrov', userId: 'u3', pair: 'ES', direction: 'short', qty: 5, entry: 5305,  current: 5285,  pnl: 500,  pnlPct: 0.19, opened: '2h ago',   platform: 'Deepchart', bot: 'Mean Reversion',   risk: 0.5 },
  { id: 't3', user: 'Aiden Wright', userId: 'u4', pair: 'GC', direction: 'long',  qty: 2, entry: 2352,  current: 2365,  pnl: 260,  pnlPct: 0.55, opened: '3h ago',   platform: 'NinjaTrader', bot: 'Sniper',          risk: 1.5, flagged: true },
  { id: 't4', user: 'Lena Voss',    userId: 'u5', pair: 'BTC', direction: 'long', qty: 1, entry: 62000, current: 61850, pnl: -150, pnlPct: -0.24, opened: '4h ago', platform: 'Coinbase', bot: 'Grid',             risk: 2.0 },
  { id: 't5', user: 'Diego Rivera', userId: 'u6', pair: 'CL', direction: 'short', qty: 1, entry: 79.5,  current: 79.5,  pnl: 0,    pnlPct: 0,     opened: '5h ago',   platform: 'ATAS',      bot: 'Trend',            risk: 0.5 },
  { id: 't6', user: 'Yuki Tanaka',  userId: 'u7', pair: 'YM', direction: 'long',  qty: 4, entry: 39050, current: 39080, pnl: 120,  pnlPct: 0.08, opened: '1h ago',   platform: 'Sierra',    bot: 'Breakout',         risk: 0.75 },
];

export function AdminTrades() {
  const [trades, setTrades] = useState(TRADES);

  function forceClose(id: string) {
    if (!confirm('Force-close this trade? User will be notified.')) return;
    setTrades((curr) => curr.filter((t) => t.id !== id));
    alert('Trade force-closed at market price');
  }

  function killSwitch(userId: string) {
    if (!confirm('EMERGENCY: Close ALL trades for this user and disable bot?')) return;
    setTrades((curr) => curr.filter((t) => t.userId !== userId));
    alert('🚨 Kill switch activated. All trades closed. Bot disabled. User notified.');
  }

  return (
    <div className="space-y-4 max-w-7xl mx-auto">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h1 className="text-[22px] font-bold text-white">Live trades</h1>
          <p className="text-[12px] text-white/50">{trades.length} open · {trades.filter((t) => t.flagged).length} flagged</p>
        </div>
        <div className="flex items-center gap-1.5">
          <button onClick={() => alert('Pause all bots globally')} data-testid="pause-all-bots" className="h-9 rounded-[8px] bg-[#FF9500] px-3 text-[12px] font-semibold text-white active:opacity-40">⏸ Pause all</button>
          <button onClick={() => { if (confirm('EMERGENCY: Close ALL open trades platform-wide?')) { setTrades([]); alert('🚨 All trades closed.'); } }} data-testid="kill-switch-all" className="h-9 rounded-[8px] bg-[#FF3B30] px-3 text-[12px] font-semibold text-white active:opacity-40">🚨 Kill switch</button>
        </div>
      </div>

      {/* Stats strip */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
        <Kpi label="Open P&L"    value={`${trades.reduce((s, t) => s + t.pnl, 0) >= 0 ? '+' : ''}$${trades.reduce((s, t) => s + t.pnl, 0).toFixed(0)}`} color={trades.reduce((s, t) => s + t.pnl, 0) >= 0 ? '#34C759' : '#FF3B30'} />
        <Kpi label="Open trades" value={trades.length.toString()} />
        <Kpi label="Volume"      value={`$${(trades.reduce((s, t) => s + t.qty * t.entry, 0) / 1000).toFixed(0)}K`} />
        <Kpi label="Avg risk"    value={`${(trades.reduce((s, t) => s + t.risk, 0) / trades.length).toFixed(1)}%`} />
      </div>

      {/* Trades list */}
      <div className="rounded-[16px] bg-[#1C1C1E] border border-white/5 overflow-hidden">
        <div className="grid grid-cols-12 gap-2 px-4 py-2.5 border-b border-white/5 bg-white/[0.02] text-[10px] font-bold uppercase text-white/40">
          <div className="col-span-2">Trader</div>
          <div className="col-span-1">Pair</div>
          <div className="col-span-1 text-right">Qty</div>
          <div className="col-span-2 text-right">Entry / Current</div>
          <div className="col-span-1 text-right">P&L</div>
          <div className="col-span-1">Bot</div>
          <div className="col-span-1">Platform</div>
          <div className="col-span-3 text-right">Actions</div>
        </div>
        {trades.map((t) => (
          <div key={t.id} data-testid={`trade-${t.id}`} className={['grid grid-cols-12 gap-2 px-4 py-3 border-b border-white/5 last:border-b-0 items-center text-[11px]', t.flagged ? 'bg-[#FF3B30]/5' : ''].join(' ')}>
            <div className="col-span-2">
              <div className="flex items-center gap-1">
                {t.flagged && <span>🚩</span>}
                <div>
                  <div className="text-white font-semibold">{t.user}</div>
                  <div className="text-[10px] text-white/40">{t.opened}</div>
                </div>
              </div>
            </div>
            <div className="col-span-1">
              <div className="font-bold text-white">{t.pair}</div>
              <div className={['text-[9px] uppercase font-bold', t.direction === 'long' ? 'text-[#34C759]' : 'text-[#FF3B30]'].join(' ')}>{t.direction}</div>
            </div>
            <div className="col-span-1 text-right text-white font-semibold tabular-nums">{t.qty}</div>
            <div className="col-span-2 text-right text-white tabular-nums">
              <div>{t.entry.toFixed(2)}</div>
              <div className={['text-[10px]', t.pnl >= 0 ? 'text-[#34C759]' : 'text-[#FF3B30]'].join(' ')}>{t.current.toFixed(2)}</div>
            </div>
            <div className={['col-span-1 text-right font-bold tabular-nums', t.pnl >= 0 ? 'text-[#34C759]' : 'text-[#FF3B30]'].join(' ')}>
              {t.pnl >= 0 ? '+' : ''}${Math.abs(t.pnl).toFixed(0)}
              <div className="text-[9px] font-normal opacity-80">{t.pnlPct.toFixed(2)}%</div>
            </div>
            <div className="col-span-1 text-white/60 truncate">{t.bot}</div>
            <div className="col-span-1 text-white/60 truncate">{t.platform}</div>
            <div className="col-span-3 flex items-center justify-end gap-1">
              <button onClick={() => forceClose(t.id)} data-testid={`force-close-${t.id}`} className="h-7 rounded-full bg-[#FF3B30]/20 px-2 text-[10px] font-semibold text-[#FF3B30] active:bg-[#FF3B30]/30">Close</button>
              <button onClick={() => killSwitch(t.userId)} data-testid={`killswitch-${t.id}`} className="h-7 rounded-full bg-[#FF9500]/20 px-2 text-[10px] font-semibold text-[#FF9500] active:bg-[#FF9500]/30">Kill</button>
              <button onClick={() => alert('Sent warning to user')} data-testid={`warn-${t.id}`} className="h-7 rounded-full bg-white/5 px-2 text-[10px] font-semibold text-white/80 active:bg-white/10">Warn</button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function Kpi({ label, value, color }: { label: string; value: string; color?: string }) {
  return (
    <div className="rounded-[12px] bg-[#1C1C1E] border border-white/5 p-3 text-center">
      <div className="text-[10px] uppercase tracking-wider text-white/40">{label}</div>
      <div className="mt-1 text-[18px] font-bold tabular-nums" style={{ color: color || '#FFFFFF' }}>{value}</div>
    </div>
  );
}
